import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'
import type { WorkspaceAction } from '../../../utils/workspacePermissions'
import type { ModuleKey } from '../../../utils/workspaceModules'
import { isPageHiddenForMe } from '../../pageVisibilityService'

export const NAVIGATION_ACTION_ID = 'navigation.surface.open'

type NavigationActionArgs = {
  target: string
  targetLabel: string
  targetRoute?: string
  workspaceSlug?: string
}

export type NavigationActionResult = { toastMessage: string }

interface RouteEntry {
  pattern: RegExp
  build: (workspaceSlug: string) => string
  permission?: WorkspaceAction
  module?: ModuleKey
}

const routeMap: RouteEntry[] = [
  { pattern: /^timeline|^dashboard/, build: (slug) => `/console/workspace/${slug}/d`, permission: 'timeline.view' },
  { pattern: /^calendar/, build: (slug) => `/console/workspace/${slug}/calendar`, permission: 'timeline.view' },
  { pattern: /^manage/, build: (slug) => `/console/workspace/${slug}/d/manage`, permission: 'timeline.edit' },
  { pattern: /^checklists?/, build: (slug) => `/console/workspace/${slug}/d/checklists`, permission: 'timeline.view' },
  { pattern: /^meetings?/, build: (slug) => `/console/workspace/${slug}/meetings`, permission: 'meetings.view', module: 'meetings' },
  { pattern: /^chat/, build: (slug) => `/console/workspace/${slug}/chat`, permission: 'chat.view' },
  { pattern: /^automations?|^automate/, build: (slug) => `/console/workspace/${slug}/automate`, permission: 'automation.view', module: 'automate' },
  { pattern: /^reports?/, build: (slug) => `/console/workspace/${slug}/d/reports`, permission: 'workspace.view_reports', module: 'reports' },
  { pattern: /^analysis|^analytics|^insights?/, build: (slug) => `/console/workspace/${slug}/analysis`, permission: 'readonly.view_analysis', module: 'analysis' },
  { pattern: /^explore/, build: () => '/console/explore' },
  { pattern: /^design/, build: (slug) => `/console/workspace/${slug}/design`, permission: 'forms.view', module: 'design' },
  { pattern: /^upload|^files?/, build: (slug) => `/console/workspace/${slug}/upload`, permission: 'integrations.view', module: 'upload' },
  { pattern: /^forms?/, build: (slug) => `/console/workspace/${slug}/forms`, permission: 'forms.view', module: 'forms' },
  { pattern: /^link|^integrations?|^hub/, build: (slug) => `/console/workspace/${slug}/link`, permission: 'integrations.view', module: 'linkHub' },
  { pattern: /^stock|^inventory/, build: (slug) => `/console/workspace/${slug}/stock`, permission: 'timeline.view', module: 'stock' },
  { pattern: /^budget|^expenses?/, build: (slug) => `/console/workspace/${slug}/budget`, permission: 'timeline.view', module: 'budget' },
  { pattern: /^quota/, build: (slug) => `/console/workspace/${slug}/quota`, permission: 'timeline.view', module: 'quota' },
  { pattern: /^teaching/, build: (slug) => `/console/workspace/${slug}/teaching`, permission: 'timeline.view', module: 'teaching' },
  { pattern: /^training/, build: (slug) => `/console/workspace/${slug}/training`, permission: 'timeline.view', module: 'training' },
  { pattern: /^timesheets?/, build: (slug) => `/console/workspace/${slug}/timesheets`, permission: 'timeline.view', module: 'timesheets' },
  { pattern: /^team|^members?|^invites?/, build: () => '/console/team' },
  { pattern: /^settings/, build: (slug) => `/console/workspace/${slug}/d/settings/admin`, permission: 'workspace.view_settings' },
  { pattern: /^overview/, build: (slug) => `/console/workspace/${slug}/overview`, permission: 'overview.view' },
  { pattern: /^for-you|^todos?/, build: (slug) => `/console/workspace/${slug}/for-you`, permission: 'timeline.view' },
  { pattern: /^focus/, build: () => '/console/focus' },
  { pattern: /^purchase|^billing/, build: () => '/console/purchase' },
  { pattern: /^ai|^magic/, build: (slug) => `/console/workspace/${slug}/d/ai`, permission: 'ai.use' },
  { pattern: /^raid/, build: (slug) => `/console/workspace/${slug}/d/raid`, permission: 'raid.participate', module: 'funPack' },
  { pattern: /^heatmap/, build: (slug) => `/console/workspace/${slug}/overview`, permission: 'overview.view' },
]

const navigationArgsSchema: JSONSchemaType<NavigationActionArgs> = {
  type: 'object',
  properties: {
    target: { type: 'string' },
    targetLabel: { type: 'string' },
    targetRoute: { type: 'string', nullable: true },
    workspaceSlug: { type: 'string', nullable: true },
  },
  required: ['target', 'targetLabel'],
  additionalProperties: false,
}

const routeFor = (target: string, workspaceSlug: string): { path: string; permission?: WorkspaceAction; module?: ModuleKey } | null => {
  const normalized = target.toLowerCase().trim()
  for (const entry of routeMap) {
    if (entry.pattern.test(normalized)) return { path: entry.build(workspaceSlug), permission: entry.permission, module: entry.module }
  }
  return null
}

const completed = (result: NavigationActionResult, message: string, navigateTo: string, correlationId: string): ActionOutcome<NavigationActionResult> => ({
  kind: 'COMPLETED', result, message, navigateTo, correlationId,
})

const navigationAction: ActionDefinition<NavigationActionArgs, NavigationActionResult> = {
  id: NAVIGATION_ACTION_ID,
  label: 'Open workspace surface',
  description: 'Opens a workspace surface by name.',
  risk: 'READ_ONLY',
  domain: 'navigation',
  surfaces: ['ai', 'catalog'],
  argsSchema: navigationArgsSchema,
  run: async (args, context) => {
    if (args.targetRoute?.trim()) {
      const message = `Opening ${args.targetLabel}.`
      return completed({ toastMessage: `Opening ${args.targetLabel}` }, message, args.targetRoute, context.correlationId!)
    }

    const route = routeFor(args.target, args.workspaceSlug?.trim() || context.workspaceId)
    if (!route) {
      return { kind: 'FAILED', message: `I do not know how to navigate to "${args.target}".`, recoverable: true, correlationId: context.correlationId! }
    }
    if (route.permission && !context.canPerform(route.permission)) {
      return {
        kind: 'BLOCKED_BY_PERMISSION',
        message: `You do not have permission (${route.permission}) to open ${args.target}.`,
        missingPermission: route.permission,
        correlationId: context.correlationId!,
      }
    }
    if (route.module && context.switchedOffModules?.has(route.module)) {
      return {
        kind: 'FAILED',
        message: `${args.target} is turned off in this workspace.`,
        recoverable: false,
        correlationId: context.correlationId!,
      }
    }
    const checkHidden = context.isPageHidden ?? ((target: string) => isPageHiddenForMe(target, { role: context.actor.role }))
    if (checkHidden(args.target)) {
      return {
        kind: 'FAILED',
        message: `${args.target} is hidden for you.`,
        recoverable: false,
        correlationId: context.correlationId!,
      }
    }

    const message = `Opening ${args.target}.`
    return completed({ toastMessage: `Opening ${args.target}` }, message, route.path, context.correlationId!)
  },
}

export const registerNavigationAction = (): void => {
  if (!getAction(NAVIGATION_ACTION_ID)) registerAction(navigationAction)
}

registerNavigationAction()
